/**
 * A-GTCC-VIEW-ALIN1 — typed client for the "Alerts & Incidents" (alin) view.
 *
 * Reference: `docs/design/GTCC_REFERENCE_2026-09-01.html:585-605`. P17 of the
 * Owner programme puts the alarm WORK here and nowhere else: the cockpit only
 * counts alarms and links into this view.
 *
 * ## What was measured before anything was declared here
 *
 * The repository already carries two alert populations, and neither of them is
 * the one the reference draws:
 *
 *   - `GET /v1/alerts` + `POST /v1/alerts/:id/ack`
 *     (`services/api/src/modules/alert-state/routes.ts`, scope `vehicle.read` /
 *     `vehicle.write`) are TENANT alerts about vehicles, rules, geofences and
 *     devices. `apps/admin/src/gtcc/views/alerts/useGtccAlerts.ts` binds them.
 *     The reference rows are hosts (`gt-backup-1`, `gt-app-main-1`, `bastion`),
 *     so this ack source cannot be reused — it acknowledges a different object.
 *   - `GET /v1/admin/data-plane/monitoring/summary` →
 *     `alerts: MonitoringAlertDto[]`
 *     (`services/api/src/modules/data-plane/monitoring-readonly/types.ts:245`)
 *     IS the right population: Alertmanager alerts, sanitised, already
 *     carrying the `owner_de_headline` annotation as `ownerText`. It is
 *     read-only by construction and carries no alert fingerprint, so it can
 *     show the table but can neither acknowledge nor own a row.
 *
 * Hence the split below, and it is the only honest one:
 *
 *   - `GET /v1/admin/gtcc/alerts/summary` — the alin table, owned by
 *     K-GTCC-API-SVC1 ("Rules & Telemetry"). Same response convention as every
 *     GTCC endpoint (`measuredAt` / `freshnessSeconds` / `status`) and the same
 *     sanitising contract as the monitoring summary it supersedes. Until K
 *     merges the route answers 404 and the view says so — `disabled`, never an
 *     invented row.
 *   - `POST /v1/admin/gtcc/alerts/:fingerprint/ack` and
 *     `PUT /v1/admin/gtcc/alerts/:fingerprint/owner` — the two writes of this
 *     view, built by this package on the server side. Owner-GO for the
 *     acknowledgement is on record; both are audited and both sit behind
 *     `requirePlatformOwner("data-plane")`.
 *
 * Read paths use `apiGet` only. The two writes are the ONLY mutation verbs in
 * this module, and each of them names its alert by the fingerprint the API
 * handed out — this module never builds a silence matcher, never talks to
 * Alertmanager and never learns an upstream URL.
 */
import { apiGet, apiSend, ApiError } from "../../lib/api.js";

/* --------------------------------------------------------------- paths */

const ALERTS_BASE = "/v1/admin/gtcc/alerts";

export const ALERTS_SUMMARY_PATH = `${ALERTS_BASE}/summary`;

/** `POST /:fingerprint/ack` — the fingerprint is the only segment appended. */
export function alertAckPath(fingerprint: string): string {
  return `${ALERTS_BASE}/${encodeURIComponent(fingerprint)}/ack`;
}

/** `PUT /:fingerprint/owner` — assignment of the row's owner. */
export function alertOwnerPath(fingerprint: string): string {
  return `${ALERTS_BASE}/${encodeURIComponent(fingerprint)}/owner`;
}

/* ---------------------------------------------------------------- types */

/** The closed status enum of the GTCC API convention. */
export const ALERT_STATUSES = ["healthy", "warning", "critical", "stale", "unmeasurable"] as const;
export type AlertStatus = (typeof ALERT_STATUSES)[number];

/** Severity of one alarm row — the dot colour of the reference. */
export const ALERT_SEVERITIES = ["critical", "warning", "info"] as const;
export type AlertSeverity = (typeof ALERT_SEVERITIES)[number];

/**
 * One row of "Aktive Alarme".
 *
 * `ownerText` is the `owner_de_headline` annotation of the firing rule — the
 * operator-readable alarm of the reference ("Auslagerung: Pruefung des Archivs
 * fehlgeschlagen"), not the implementation alert name. When a rule carries no
 * Owner copy the field is `null` and the table falls back to `alertname`
 * rather than inventing a sentence.
 *
 * `ageSeconds` arrives decided by the API (`SEIT` in the reference). Nothing in
 * this view reads a local clock, so a stale source can never look recent.
 */
export interface GtccAlertRowDto {
  /** Alertmanager fingerprint — the identity of this alert instance. */
  readonly fingerprint: string;
  readonly alertname: string;
  readonly severity: AlertSeverity;
  /** German Owner copy of the rule; `null` when the rule has none. */
  readonly ownerText: string | null;
  /** Sanitised host alias (`gt-backup-1`), never an IP, never a port. */
  readonly system: string | null;
  readonly startsAt: string | null;
  readonly ageSeconds: number | null;
  /** Display name of the assigned owner; `null` renders the em dash. */
  readonly owner: string | null;
  /** `true` once an acknowledging silence is active for this alert. */
  readonly acknowledged: boolean;
}

/**
 * One row of "Incident-Arbeitsraum": related alarms bundled into one incident
 * with its context line (last good run, affected rules, runbook link).
 */
export interface GtccIncidentDto {
  readonly id: string;
  readonly name: string;
  readonly severity: AlertSeverity;
  /** The context sentence of the reference; the API composes it. */
  readonly context: string;
  /** Runbook target; `null` hides the link instead of linking nowhere. */
  readonly runbookUrl: string | null;
}

/** One active silence of "Stumm-Schaltungen (begrenzt)". */
export interface GtccSilenceDto {
  readonly id: string;
  /** What the silence covers, in the sanitised Owner wording. */
  readonly label: string;
  /** Mandatory reason — a silence without one is never created. */
  readonly reason: string;
  /** Remaining runtime; the API decides it, the view only formats it. */
  readonly remainingSeconds: number;
}

export interface GtccAlertsSummaryDto {
  readonly measuredAt: string;
  readonly freshnessSeconds?: number;
  readonly status: AlertStatus;
  readonly alerts: ReadonlyArray<GtccAlertRowDto>;
  readonly incidents: ReadonlyArray<GtccIncidentDto>;
  readonly silences: ReadonlyArray<GtccSilenceDto>;
}

/**
 * Owner decision, carried by the write contract rather than by this module:
 * an acknowledging silence lasts at most 24 h and always carries a reason.
 * The server enforces both; the constant exists so the view can label the
 * control with the same number the reference prints.
 */
export const ACK_MAX_HOURS = 24;

export interface GtccAlertAckRequest {
  /** Free-text reason; the server rejects an empty one. */
  readonly reason: string;
  /** Silence duration in hours, `1 … ACK_MAX_HOURS`. */
  readonly hours: number;
}

export interface GtccAlertOwnerRequest {
  /** `null` clears the assignment and puts the em dash back. */
  readonly owner: string | null;
}

/* --------------------------------------------------------------- result */

/**
 * Same discriminated union as `./fleet.ts` and
 * `apps/admin/src/monitoring/api.ts:34`: the view never sees an `ApiError`.
 * `disabled` is the honest answer while K-GTCC-API-SVC1 is unmerged.
 */
export type AlertsResult<T> =
  | { readonly ok: true; readonly data: T }
  | { readonly ok: false; readonly kind: "disabled"; readonly httpStatus: 404 }
  | { readonly ok: false; readonly kind: "forbidden"; readonly httpStatus: 403 }
  | {
      readonly ok: false;
      readonly kind: "upstream";
      readonly httpStatus: number;
      readonly requestId: string | undefined;
    }
  | { readonly ok: false; readonly kind: "transport_error"; readonly message: string };

function toFailure<T>(err: unknown): AlertsResult<T> {
  if (err instanceof ApiError) {
    const { status, requestId } = err;
    if (status === 404) return { ok: false, kind: "disabled", httpStatus: 404 };
    if (status === 403) return { ok: false, kind: "forbidden", httpStatus: 403 };
    return { ok: false, kind: "upstream", httpStatus: status, requestId };
  }
  const message = err instanceof Error ? err.message : "gtcc alerts request failed";
  return { ok: false, kind: "transport_error", message };
}

/**
 * The three lists the view walks. A body that does not carry them is not an
 * empty measurement — it is an answer this client cannot read, and saying
 * "keine Alarme" to it would report a broken source as a healthy one
 * (Owner honesty P8/P9/P10). It is reported as a failure instead, which also
 * keeps a malformed body from reaching the table as `undefined`.
 */
function hasSummaryShape(body: unknown): body is GtccAlertsSummaryDto {
  if (body === null || typeof body !== "object") return false;
  const candidate = body as Partial<GtccAlertsSummaryDto>;
  return (
    Array.isArray(candidate.alerts) &&
    Array.isArray(candidate.incidents) &&
    Array.isArray(candidate.silences)
  );
}

export async function fetchGtccAlertsSummary(
  signal?: AbortSignal,
): Promise<AlertsResult<GtccAlertsSummaryDto>> {
  try {
    const body = await apiGet<GtccAlertsSummaryDto>(ALERTS_SUMMARY_PATH, undefined, signal);
    if (!hasSummaryShape(body)) {
      return {
        ok: false,
        kind: "transport_error",
        message: `${ALERTS_SUMMARY_PATH} answered without alerts/incidents/silences`,
      };
    }
    return { ok: true, data: body };
  } catch (err) {
    return toFailure<GtccAlertsSummaryDto>(err);
  }
}

/**
 * Acknowledge one alarm: an Alertmanager silence with reason and duration,
 * capped at {@link ACK_MAX_HOURS} by the server. Answers the updated row so the
 * table can show "✓ Ack" without a second read.
 */
export async function ackGtccAlert(
  fingerprint: string,
  request: GtccAlertAckRequest,
  signal?: AbortSignal,
): Promise<AlertsResult<GtccAlertRowDto>> {
  try {
    return {
      ok: true,
      data: await apiSend<GtccAlertRowDto>("POST", alertAckPath(fingerprint), request, signal),
    };
  } catch (err) {
    return toFailure<GtccAlertRowDto>(err);
  }
}

/** Assign (or clear) the owner of one alarm. */
export async function setGtccAlertOwner(
  fingerprint: string,
  request: GtccAlertOwnerRequest,
  signal?: AbortSignal,
): Promise<AlertsResult<GtccAlertRowDto>> {
  try {
    return {
      ok: true,
      data: await apiSend<GtccAlertRowDto>("PUT", alertOwnerPath(fingerprint), request, signal),
    };
  } catch (err) {
    return toFailure<GtccAlertRowDto>(err);
  }
}

/* ----------------------------------------------------------- query keys */

/*
 * Prefix convention of the GTCC query-key inventory: `["gtcc", <viewkey>, …]`.
 * The registry pins `alin` on `["gtcc", "alerts"]`, so that is the prefix here
 * — an own exported literal, for the same static-readability reason spelled
 * out in `./fleet.ts`.
 */
export const ALERTS_SUMMARY_KEY = ["gtcc", "alerts", "gtcc-summary"] as const;

export const ALERTS_QUERY_KEYS = {
  summary: ALERTS_SUMMARY_KEY,
} as const;
